import qc from 'qc'
import {
  convertCoordinateByWorldScale,
  getCarouselComponentNodeByChildNode,
  getNodeWorldPosition,
  getSceneNodeByNode,
  isCarouselResourceGroupNode,
  isCarouselPiteGroupNode,
  isCarouselShadowNode,
} from '@ps/bridge/qc/base'
import { moveNodeByWorldPosition } from '@ps/bridge/qc/node'
import _ from 'lodash'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import shortid from 'shortid'
import { getShapeByNode } from '@ps/store/workbench/stage/utils/helper'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import { scripts } from '@ps/bridge/qc/scripts'
import { switchArrayToObject } from '@ps/bridge/qc/scripts/carousel'
import { removeUnSplitProInLayout } from '@ps/bridge/qc/node/layout'
import nodePool from '@ps/bridge/qc/node-pool'

export const setNodePosition = (
  node: qc.Node,
  x?: number,
  y?: number,
): void => {
  const worldPosition = getNodeWorldPosition(node)
  const factPosition = convertCoordinateByWorldScale({
    x: !_.isNil(x) ? Math.trunc(x) : worldPosition.x,
    y: !_.isNil(y) ? Math.trunc(y) : worldPosition.y,
  })
  moveNodeByWorldPosition({
    node: node,
    x: factPosition.x,
    y: factPosition.y,
  })
}

export const addCarouselNodeTagList = ({
  carouselNode,
  tag,
}: {
  carouselNode: qc.Node
  tag: string
}) => {
  const data = editorScript.getScript(carouselNode)
  if (!data.$data.carouselTagList) {
    data.$data.carouselTagList = []
  }
  data.$data.carouselTagList.push({
    id: shortid.generate(),
    name: tag,
  })
}

export const removeCarouselNodeTag = ({
  carouselNode,
  tagId,
}: {
  carouselNode: qc.Node
  tagId: string
}) => {
  const data = editorScript.getScript(carouselNode)
  if (!data.$data.carouselTagList) {
    data.$data.carouselTagList = []
  }
  data.$data.carouselTagList = data.$data.carouselTagList.filter(
    (item) => item.id !== tagId,
  )
  const dataCarousel = editorScript.getScript(carouselNode)
  if (!dataCarousel.$data.tagMap) dataCarousel.$data.tagMap = {}
  delete dataCarousel.$data.tagMap[tagId]
}

export function refreshTagMap({
  oldUuid,
  newUuid,
  carouselNode,
}: {
  oldUuid: string
  newUuid: string
  carouselNode: qc.Node
}) {
  const dataCarousel = editorScript.getScript(carouselNode)
  if (!dataCarousel.$data.tagMap) dataCarousel.$data.tagMap = {}
  for (const key in dataCarousel.$data.tagMap) {
    dataCarousel.$data.tagMap[key].replace(oldUuid, newUuid)
  }
}

export const setCarouselResourceTag = ({
  node,
  carouselNode,
  tags,
}: {
  node: qc.Node
  carouselNode: qc.Node
  tags: string[]
}) => {
  const dataCarousel = editorScript.getScript(carouselNode)
  if (!dataCarousel.$data.tagMap) dataCarousel.$data.tagMap = {}
  // 清理节点原来标签数据
  for (const key in dataCarousel.$data.tagMap) {
    if (dataCarousel.$data.tagMap[key].includes(node.uniqueName)) {
      const temp = dataCarousel.$data.tagMap[key].filter(
        (item) => item !== node.uniqueName,
      )
      if (temp.length === 0) {
        delete dataCarousel.$data.tagMap[key]
      } else {
        dataCarousel.$data.tagMap[key] = dataCarousel.$data.tagMap[key].filter(
          (item) => item !== node.uniqueName,
        )
      }
    }
  }
  if (tags.length) {
    tags.forEach((tag) => {
      dataCarousel.$data.tagMap[tag] = [
        ...(dataCarousel.$data.tagMap[tag] || []),
        node.uniqueName,
      ]
    })
  }
}

export const getResourceTag = ({
  node,
  carouselNode,
}: {
  node: qc.Node
  carouselNode: qc.Node
}) => {
  const tag = []
  const dataCarousel = editorScript.getScript(carouselNode)
  if (!dataCarousel.$data.tagMap) dataCarousel.$data.tagMap = {}
  for (const i in dataCarousel.$data.tagMap) {
    if (dataCarousel.$data.tagMap[i].includes(node.uniqueName)) {
      tag.push(i)
    }
  }
  return tag
}

// 获取轮播组
export const getCarouselGroup = (node: qc.Node, carouselNode: qc.Node) => {
  const scriptData = carouselNode.getScript(scripts.carousel.name)
  const carouselGroups = [...Object.values(scriptData.carouselGroups)]
  return carouselGroups.find((item) => item.carouselId === node.uuid)
}

// 更新轮播组属性
export const setCarouselGroup = ({
  node,
  carouselNode,
  prop,
  value,
}: {
  node: qc.Node
  carouselNode: qc.Node
  prop: string
  value: any
}) => {
  const scriptData = carouselNode.getScript(scripts.carousel.name)
  const carouselGroups = [...Object.values(scriptData.carouselGroups)]
  const carouselGroup = carouselGroups.find(
    (item) => item.carouselId === node.uuid,
  )
  carouselGroup[prop] = value
  scriptData.carouselGroups = switchArrayToObject(carouselGroups)
}

// 轮播组件屏蔽group级的子节点的画布操作
export const carouselComponentCanvasHandler = (node: qc.Node) => {
  // 也要延迟一阵，不知道哪里又把shape的visible设为true了
  setTimeout(() => {
    const carouselNode = getCarouselComponentNodeByChildNode(node)
    if (!carouselNode) return
    let shape = getShapeByNode(carouselNode)
    if (shape) {
      shape.visible(false)
    }
    const children = getDescendants(carouselNode)
    children.forEach((child) => {
      const ignoreList = [isCarouselResourceGroupNode, isCarouselPiteGroupNode]
      if (ignoreList.some((val) => val(child) || val(child.parent))) {
        shape = getShapeByNode(child)
        if (shape) {
          shape.visible(false)
        }
      }
    })
    const sceneNode = getSceneNodeByNode(node)
    if (sceneNode) {
      shape = getShapeByNode(sceneNode)
      if (shape) {
        shape.visible(false)
      }
    }
  }, 20)
}

export const carouselSaveHandler = (nodes: qc.Node[]) => {
  nodes.forEach((node) => {
    if (isCarouselResourceGroupNode(node) || isCarouselShadowNode(node)) {
      removeUnSplitProInLayout(node)
    }
  })
}

export const transNumToRealSize = (
  value: number,
  pitesiteUuid: any,
  key: 'displayWidth' | 'displayHeight' | 'displaySpace',
  reChange = false,
) => {
  // return value
  const node = nodePool.find(pitesiteUuid)
  const temp = {
    displayWidth: node.width / 100,
    displayHeight: node.height / 100,
    displaySpace: node.width / 100,
  }
  if (reChange) {
    return (value / temp[key]).toFixed(0)
  }
  return (value * temp[key]).toFixed(0)
}
